import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ContentBox from '../ContentBox';
import {
    MONTH_NAMES,
    addDaysToKey,
    dayKey,
    detectTimeZone,
    isValidZone,
    listTimeZones,
    monthGrid,
    offsetLabel,
    readableDay,
    timeLabel,
    toInputValues,
    zonedToUtc
} from '../../utils/calendarTime';
import {
    createStaffCalendarEntry,
    deleteStaffCalendarEntry,
    getStaffCalendar,
    getStaffEvents,
    updateStaffCalendarEntry
} from '../../services/api';
import { typeColor } from './events/eventsShared';

const ZONE_KEY = 'franklyStaffCalendarZone';
const SHOW_EVENTS_KEY = 'franklyStaffCalendarShowEvents';
const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const REPEAT_OPTIONS = [
    { value: 'none', label: 'Does not repeat' },
    { value: 'weekly', label: 'Every week' },
    { value: 'monthly', label: 'Every month' },
    { value: 'yearly', label: 'Every year' }
];

const REPEAT_LABELS = {
    weekly: 'Weekly',
    monthly: 'Monthly',
    yearly: 'Yearly'
};

const readStoredZone = () => {
    try {
        const stored = window.localStorage.getItem(ZONE_KEY);
        if (stored && isValidZone(stored)) return stored;
    } catch {}
    return detectTimeZone();
};

const readShowEvents = () => {
    try {
        return window.localStorage.getItem(SHOW_EVENTS_KEY) !== 'false';
    } catch {}
    return true;
};

const asOccurrence = (event) => ({
    id: event.id,
    occurrenceId: `event:${event.id}`,
    title: event.title,
    notes: '',
    allDay: false,
    startAt: event.startTime,
    endAt: event.endTime,
    occurrenceStart: event.startTime,
    occurrenceEnd: event.endTime,
    repeatRule: 'none',
    isRepeat: false,
    tint: typeColor(event.type),
    teamName: event.type,
    teamRole: 'Hotel event',
    createdBy: event.addedBy,
    createdByName: event.addedByName || 'Unknown',
    isEvent: true,
    host: event.host,
    roomName: event.roomName,
    roomOwner: event.roomOwner,
    prize: event.prize
});

const occurrenceKey = (occurrence, timeZone) => (
    occurrence.allDay
        ? occurrence.occurrenceStart.slice(0, 10)
        : dayKey(occurrence.occurrenceStart, timeZone)
);

const blankForm = (dateKey) => ({
    title: '',
    notes: '',
    allDay: false,
    date: dateKey,
    startTime: '18:00',
    endTime: '',
    repeatRule: 'none',
    repeatUntil: ''
});

function EntryEditor({ occurrence, dateKey, timeZone, onClose, onSaved }) {
    const [form, setForm] = useState(() => {
        if (!occurrence) return blankForm(dateKey);

        const start = occurrence.allDay
            ? { date: occurrence.startAt.slice(0, 10), time: '00:00' }
            : toInputValues(occurrence.startAt, timeZone);

        return {
            title: occurrence.title,
            notes: occurrence.notes || '',
            allDay: occurrence.allDay,
            date: start.date,
            startTime: start.time,
            endTime: occurrence.endAt && !occurrence.allDay
                ? toInputValues(occurrence.endAt, timeZone).time
                : '',
            repeatRule: occurrence.repeatRule,
            repeatUntil: occurrence.repeatUntil || ''
        };
    });

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const set = (patch) => setForm(prev => ({ ...prev, ...patch }));

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!form.title.trim()) {
            setError('Give it a title.');
            return;
        }
        if (!form.date) {
            setError('Pick a date.');
            return;
        }

        let startAt;
        let endAt = null;

        if (form.allDay) {
            startAt = `${form.date}T00:00:00.000Z`;
        } else {
            const start = zonedToUtc(form.date, form.startTime || '00:00', timeZone);
            if (!start) {
                setError('That start time is not valid.');
                return;
            }
            startAt = start.toISOString();

            if (form.endTime) {
                const sameDay = zonedToUtc(form.date, form.endTime, timeZone);
                const end = sameDay.getTime() > start.getTime()
                    ? sameDay
                    : zonedToUtc(addDaysToKey(form.date, 1), form.endTime, timeZone);
                endAt = end.toISOString();
            }
        }

        const payload = {
            title: form.title.trim(),
            notes: form.notes.trim(),
            allDay: form.allDay,
            repeatRule: form.repeatRule,
            repeatUntil: form.repeatRule === 'none' ? null : (form.repeatUntil || null),
            startAt,
            endAt
        };

        setSaving(true);
        setError('');

        const result = occurrence
            ? await updateStaffCalendarEntry(occurrence.id, payload)
            : await createStaffCalendarEntry(payload);

        setSaving(false);

        if (!result?.success) {
            setError(result?.message || 'That did not save.');
            return;
        }

        onSaved();
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

            <div className="relative z-[201] mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto border-2 border-gray-800 bg-offwhite p-5">
                <h2 className="mb-3 font-['Jersey_10'] text-[20px] uppercase tracking-[1px] text-maroon">
                    {occurrence ? 'Edit calendar entry' : 'New calendar entry'}
                </h2>
                <hr className="mb-4 border-gold" />

                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label className="mb-1 block text-sm uppercase">Title</label>
                        <input
                            type="text"
                            value={form.title}
                            maxLength={120}
                            onChange={(e) => set({ title: e.target.value })}
                            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                            placeholder="What is happening?"
                        />
                    </div>

                    <div className="mb-3">
                        <label className="mb-1 block text-sm uppercase">Notes</label>
                        <textarea
                            value={form.notes}
                            maxLength={1000}
                            rows="3"
                            onChange={(e) => set({ notes: e.target.value })}
                            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Anything else the team should know"
                        />
                    </div>

                    <div className="mb-3">
                        <label className="flex cursor-pointer items-center gap-2 text-sm uppercase">
                            <input
                                type="checkbox"
                                checked={form.allDay}
                                onChange={(e) => set({ allDay: e.target.checked })}
                            />
                            All day
                        </label>
                    </div>

                    <div className={`mb-3 grid gap-3 ${form.allDay ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-3'}`}>
                        <div>
                            <label className="mb-1 block text-sm uppercase">Date</label>
                            <input
                                type="date"
                                value={form.date}
                                onChange={(e) => set({ date: e.target.value })}
                                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                            />
                        </div>

                        {!form.allDay && (
                            <>
                                <div>
                                    <label className="mb-1 block text-sm uppercase">Starts</label>
                                    <input
                                        type="time"
                                        value={form.startTime}
                                        onChange={(e) => set({ startTime: e.target.value })}
                                        className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm uppercase">Ends</label>
                                    <input
                                        type="time"
                                        value={form.endTime}
                                        onChange={(e) => set({ endTime: e.target.value })}
                                        className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {!form.allDay && (
                        <p className="mb-3 text-[12px] text-gray-500">
                            Times are entered in {timeZone} ({offsetLabel(timeZone)}) and stored in UTC.
                        </p>
                    )}

                    <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                            <label className="mb-1 block text-sm uppercase">Repeats</label>
                            <select
                                value={form.repeatRule}
                                onChange={(e) => set({ repeatRule: e.target.value })}
                                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                            >
                                {REPEAT_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        {form.repeatRule !== 'none' && (
                            <div>
                                <label className="mb-1 block text-sm uppercase">Until (optional)</label>
                                <input
                                    type="date"
                                    value={form.repeatUntil}
                                    onChange={(e) => set({ repeatUntil: e.target.value })}
                                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                                />
                            </div>
                        )}
                    </div>

                    {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

                    <div className="flex flex-wrap gap-2">
                        <button
                            type="submit"
                            disabled={saving}
                            className="rounded bg-maroon px-4 py-2 font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white transition-all hover:opacity-90 disabled:opacity-60"
                            style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.3)' }}
                        >
                            {saving ? 'Saving...' : occurrence ? 'Save changes' : 'Add to calendar'}
                        </button>

                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded bg-gray-200 px-4 py-2 font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-gray-800 transition-all hover:bg-gray-300"
                            style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function StaffCalendar({ userData }) {
    const [timeZone, setTimeZone] = useState(readStoredZone);
    const [zones] = useState(() => listTimeZones());

    const today = useMemo(() => dayKey(new Date(), timeZone), [timeZone]);

    const [view, setView] = useState(() => {
        const [year, month] = today.split('-').map(Number);
        return { year, month: month - 1 };
    });

    const [selected, setSelected] = useState(today);
    const [occurrences, setOccurrences] = useState([]);
    const [hotelEvents, setHotelEvents] = useState([]);
    const [showEvents, setShowEvents] = useState(readShowEvents);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editor, setEditor] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);

    const cells = useMemo(() => monthGrid(view.year, view.month), [view]);

    useEffect(() => {
        try { window.localStorage.setItem(ZONE_KEY, timeZone); } catch {}
    }, [timeZone]);

    useEffect(() => {
        try { window.localStorage.setItem(SHOW_EVENTS_KEY, String(showEvents)); } catch {}
    }, [showEvents]);

    useEffect(() => {
        let active = true;

        const from = zonedToUtc(cells[0].key, '00:00', timeZone);
        const to = zonedToUtc(addDaysToKey(cells[cells.length - 1].key, 1), '00:00', timeZone);

        setLoading(true);

        getStaffCalendar({ from: from.toISOString(), to: to.toISOString() })
            .then(result => {
                if (!active) return;

                if (result?.success) {
                    setOccurrences(result.occurrences || []);
                    setError('');
                } else {
                    setError(result?.message || 'Could not load the calendar.');
                }
                setLoading(false);
            })
            .catch(() => {
                if (!active) return;
                setError('Could not load the calendar.');
                setLoading(false);
            });

        return () => { active = false; };
    }, [cells, timeZone, reloadToken]);

    useEffect(() => {
        if (!showEvents) {
            setHotelEvents([]);
            return undefined;
        }

        let active = true;

        const from = zonedToUtc(cells[0].key, '00:00', timeZone);
        const to = zonedToUtc(addDaysToKey(cells[cells.length - 1].key, 1), '00:00', timeZone);

        getStaffEvents({ from: from.toISOString(), to: to.toISOString() })
            .then(result => {
                if (!active) return;
                setHotelEvents(result?.success ? (result.events || []) : []);
            })
            .catch(() => {
                if (active) setHotelEvents([]);
            });

        return () => { active = false; };
    }, [cells, timeZone, showEvents, reloadToken]);

    const byDay = useMemo(() => {
        const map = new Map();

        const merged = [...occurrences, ...hotelEvents.map(asOccurrence)];

        for (const occurrence of merged) {
            const key = occurrenceKey(occurrence, timeZone);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(occurrence);
        }

        for (const list of map.values()) {
            list.sort((a, b) => {
                if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
                return a.occurrenceStart.localeCompare(b.occurrenceStart);
            });
        }

        return map;
    }, [occurrences, hotelEvents, timeZone]);

    const selectedEntries = byDay.get(selected) || [];

    const shiftMonth = (delta) => {
        setView(prev => {
            const next = new Date(Date.UTC(prev.year, prev.month + delta, 1));
            return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
        });
    };

    const jumpToToday = () => {
        const [year, month] = today.split('-').map(Number);
        setView({ year, month: month - 1 });
        setSelected(today);
    };

    const canManage = useCallback(
        (occurrence) => !occurrence.isEvent
            && (userData?.hasAdminRole === true || occurrence.createdBy === userData?.userId),
        [userData]
    );

    const handleDelete = async (occurrence) => {
        if (!window.confirm(`Remove "${occurrence.title}" from the calendar?`)) return;

        const result = await deleteStaffCalendarEntry(occurrence.id);
        if (!result?.success) {
            setError(result?.message || 'Could not remove that entry.');
            return;
        }

        setReloadToken(token => token + 1);
    };

    const handleSaved = () => {
        setEditor(null);
        setReloadToken(token => token + 1);
    };

    return (
        <ContentBox
            theme="default"
            tint="lightskyblue"
            header="Staff Calendar"
            headerRight={
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => shiftMonth(-1)}
                        aria-label="Previous month"
                        className="rounded bg-black/25 px-2 py-[2px] font-['Jersey_10'] text-[16px] leading-none text-white transition-all hover:bg-black/40"
                    >
                        &lt;
                    </button>
                    <button
                        type="button"
                        onClick={jumpToToday}
                        className="rounded bg-black/25 px-2 py-[3px] font-['Jersey_10'] text-[14px] uppercase leading-none tracking-[1px] text-white transition-all hover:bg-black/40"
                    >
                        Today
                    </button>
                    <button
                        type="button"
                        onClick={() => shiftMonth(1)}
                        aria-label="Next month"
                        className="rounded bg-black/25 px-2 py-[2px] font-['Jersey_10'] text-[16px] leading-none text-white transition-all hover:bg-black/40"
                    >
                        &gt;
                    </button>
                </div>
            }
        >
            <div className="mb-2 flex items-baseline justify-between gap-2">
                <span className="font-['Jersey_10'] text-[18px] uppercase tracking-[1px] text-maroon">
                    {MONTH_NAMES[view.month]} {view.year}
                </span>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1 text-[12px] uppercase tracking-[1px] text-gray-500">
                        <input
                            type="checkbox"
                            checked={showEvents}
                            onChange={(e) => setShowEvents(e.target.checked)}
                        />
                        Hotel events
                    </label>
                    <span className="text-[12px] text-gray-500">{offsetLabel(timeZone)}</span>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-[2px] text-center">
                {WEEKDAYS.map((day, index) => (
                    <span key={index} className="py-1 font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-500">
                        {day}
                    </span>
                ))}

                {cells.map(cell => {
                    const entries = byDay.get(cell.key) || [];
                    const isSelected = cell.key === selected;
                    const isToday = cell.key === today;

                    return (
                        <button
                            key={cell.key}
                            type="button"
                            onClick={() => setSelected(cell.key)}
                            className={`flex min-h-[38px] flex-col items-center justify-start rounded px-1 pt-1 pb-[3px] transition-all ${
                                isSelected ? 'bg-maroon text-white' : cell.inMonth ? 'bg-gray-100 text-gray-800 hover:bg-gray-200' : 'bg-transparent text-gray-400 hover:bg-gray-100'
                            }`}
                            style={isToday && !isSelected ? { boxShadow: 'inset 0 0 0 2px #a52a2a' } : undefined}
                        >
                            <span className="font-['Jersey_10'] text-[15px] leading-none">{cell.day}</span>

                            <span className="mt-[3px] flex h-[5px] items-center gap-[2px]">
                                {entries.slice(0, 3).map(entry => (
                                    <span
                                        key={entry.occurrenceId}
                                        className="block h-[5px] w-[5px] rounded-full"
                                        style={{ backgroundColor: entry.tint }}
                                    />
                                ))}
                                {entries.length > 3 && (
                                    <span className={`text-[10px] leading-none ${isSelected ? 'text-white' : 'text-gray-500'}`}>+</span>
                                )}
                            </span>
                        </button>
                    );
                })}
            </div>

            <hr className="my-3 border-gray-200" />

            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-maroon">
                    {readableDay(selected)}
                </span>
                {loading && <span className="text-[12px] text-gray-500">Loading...</span>}
            </div>

            {error && <p className="mb-2 text-sm text-red-600">{error}</p>}

            {selectedEntries.length === 0 ? (
                <p className="mb-3 text-[14px] text-gray-600">Nothing on this day.</p>
            ) : (
                <ul className="mb-3 flex max-h-[280px] flex-col gap-2 overflow-y-auto pr-1">
                    {selectedEntries.map(entry => (
                        <li
                            key={entry.occurrenceId}
                            className="rounded bg-gray-100 py-2 pl-2 pr-2"
                            style={{ borderLeft: `4px solid ${entry.tint}` }}
                        >
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                <span className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-700">
                                    {entry.allDay
                                        ? 'All day'
                                        : `${timeLabel(entry.occurrenceStart, timeZone)}${entry.occurrenceEnd ? ` - ${timeLabel(entry.occurrenceEnd, timeZone)}` : ''}`}
                                </span>

                                {entry.isRepeat && (
                                    <span className="rounded bg-gray-300 px-[6px] py-[1px] text-[11px] uppercase tracking-[1px] text-gray-700">
                                        {REPEAT_LABELS[entry.repeatRule]}
                                    </span>
                                )}
                            </div>

                            <p className="break-words text-[15px] text-gray-800">{entry.title}</p>

                            {entry.isEvent && (
                                <p className="mt-1 break-words text-[13px] text-gray-600">
                                    {entry.host} in {entry.roomName}
                                    {entry.prize ? `, win ${entry.prize}` : ''}
                                </p>
                            )}

                            {entry.notes && (
                                <p className="mt-1 whitespace-pre-wrap break-words text-[13px] text-gray-600">{entry.notes}</p>
                            )}

                            <div className="mt-1 flex flex-wrap items-center gap-2">
                                <span
                                    className="rounded px-[6px] py-[1px] font-['Jersey_10'] text-[13px] uppercase leading-none tracking-[1px] text-white"
                                    style={{ backgroundColor: entry.tint }}
                                    title={entry.teamRole || undefined}
                                >
                                    {entry.teamName}
                                </span>
                                <span className="text-[12px] text-gray-500">{entry.createdByName}</span>

                                {entry.isEvent && (
                                    <Link
                                        to="/staff/events"
                                        className="ml-auto text-[12px] uppercase tracking-[1px] text-gray-600 underline hover:text-maroon"
                                    >
                                        Schedule
                                    </Link>
                                )}

                                {canManage(entry) && (
                                    <span className="ml-auto flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setEditor({ occurrence: entry })}
                                            className="text-[12px] uppercase tracking-[1px] text-gray-600 underline hover:text-maroon"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(entry)}
                                            className="text-[12px] uppercase tracking-[1px] text-gray-600 underline hover:text-red-600"
                                        >
                                            Remove
                                        </button>
                                    </span>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            <button
                type="button"
                onClick={() => setEditor({ occurrence: null })}
                className="w-full rounded bg-gray-200 px-3 py-2 font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-gray-800 transition-all hover:bg-gray-300"
                style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' }}
            >
                Add to calendar
            </button>

            <label className="mt-3 block text-[12px] uppercase tracking-[1px] text-gray-500">
                Your timezone
                <select
                    value={timeZone}
                    onChange={(e) => setTimeZone(e.target.value)}
                    className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-[13px] normal-case tracking-normal text-gray-800"
                >
                    {zones.map(zone => (
                        <option key={zone} value={zone}>
                            {zone.replace(/_/g, ' ')} ({offsetLabel(zone)})
                        </option>
                    ))}
                </select>
            </label>

            {editor && (
                <EntryEditor
                    occurrence={editor.occurrence}
                    dateKey={selected}
                    timeZone={timeZone}
                    onClose={() => setEditor(null)}
                    onSaved={handleSaved}
                />
            )}
        </ContentBox>
    );
}

export default StaffCalendar;
